import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import config from "@/config";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/utils";

export interface StediEligibilityResponsePayload {
  case_id: string;
  patient_id: string;
  member_id: string;
  enriched_at: string | null;
  /** Provider NPI on the case document when the response was fetched. */
  provider_npi_from_case?: string | null;
  /** NPI sent on the last live Stedi request (stored on patient enrichment). */
  request_provider_npi?: string | null;
  /** Ordered payer (trading partner) ids queried for this case: primary first, then fallbacks. */
  attempted_payer_ids?: string[] | null;
  stedi_eligibility_response: Record<string, unknown>;
}

export interface StediRerunResponse {
  case_id: string;
  success: boolean;
  enriched: boolean;
  has_stedi_eligibility_response: boolean;
  provider_npi_used?: string | null;
  trading_partner_service_id_used?: string | null;
  enriched_at: string | null;
}

export type StediRerunOptions = {
  tradingPartnerServiceId?: string;
  searchProviderNpi?: string;
};

type Props = {
  caseId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

async function fetchStediResponse(
  caseId: string,
): Promise<StediEligibilityResponsePayload> {
  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/intake/case/${encodeURIComponent(caseId)}/stedi-response`,
  );
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string" && data.detail.trim()) {
        detail = data.detail;
      }
    } catch {
      // ignore non-JSON body
    }
    throw new Error(detail);
  }
  return (await res.json()) as StediEligibilityResponsePayload;
}

export async function rerunStediEnrichment(
  caseId: string,
  options?: StediRerunOptions,
): Promise<StediRerunResponse> {
  const tradingPartnerServiceId = options?.tradingPartnerServiceId?.trim();
  const searchProviderNpi = options?.searchProviderNpi?.trim();
  const body: Record<string, string> = {};
  if (tradingPartnerServiceId) {
    body.trading_partner_service_id = tradingPartnerServiceId;
  }
  if (searchProviderNpi) {
    body.search_provider_npi = searchProviderNpi;
  }

  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/intake/case/${encodeURIComponent(caseId)}/stedi-rerun`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string" && data.detail.trim()) {
        detail = data.detail;
      }
    } catch {
      // ignore non-JSON body
    }
    throw new Error(detail);
  }
  return (await res.json()) as StediRerunResponse;
}

type StediRerunDialogProps = {
  caseId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  running: boolean;
  onConfirm: (options?: StediRerunOptions) => void | Promise<void>;
};

export function StediRerunDialog({
  caseId,
  open,
  onOpenChange,
  running,
  onConfirm,
}: StediRerunDialogProps) {
  const [stediId, setStediId] = useState("");
  const [searchNpi, setSearchNpi] = useState("");

  useEffect(() => {
    if (open) {
      setStediId("");
      setSearchNpi("");
    }
  }, [open, caseId]);

  const handleSubmit = useCallback(() => {
    const trimmedStediId = stediId.trim();
    const trimmedSearchNpi = searchNpi.trim();
    const options: StediRerunOptions = {};
    if (trimmedStediId) {
      options.tradingPartnerServiceId = trimmedStediId;
    }
    if (trimmedSearchNpi) {
      options.searchProviderNpi = trimmedSearchNpi;
    }
    void onConfirm(Object.keys(options).length > 0 ? options : undefined);
  }, [onConfirm, searchNpi, stediId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Re-run Stedi eligibility check</DialogTitle>
          <DialogDescription>
            {caseId ? (
              <>
                Run a live Stedi check for case{" "}
                <span className="font-mono text-xs">{caseId}</span>. Leave the
                Stedi id blank to resolve from the payer directory / payer
                search.
              </>
            ) : (
              "Select a case to re-run Stedi."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="stedi-trading-partner-id"
              className="text-sm font-medium leading-none"
            >
              Stedi id (optional)
            </label>
            <Input
              id="stedi-trading-partner-id"
              value={stediId}
              onChange={(e) => setStediId(e.target.value)}
              placeholder="e.g. QDTRP"
              className="font-mono"
              disabled={running}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !running) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              When set, this value is sent as{" "}
              <span className="font-mono">tradingPartnerServiceId</span> on the
              eligibility request.
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="stedi-search-provider-npi"
              className="text-sm font-medium leading-none"
            >
              Search NPI (optional)
            </label>
            <Input
              id="stedi-search-provider-npi"
              value={searchNpi}
              onChange={(e) => setSearchNpi(e.target.value)}
              placeholder="e.g. 1760431845"
              className="font-mono"
              disabled={running}
              inputMode="numeric"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !running) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              When set, this NPI is used on the eligibility request only. It is
              not saved on the case or patient record.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={running || !caseId}>
            {running ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Running…
              </>
            ) : (
              "Run check"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StediResponseModal({ caseId, open, onOpenChange }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<StediEligibilityResponsePayload | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !caseId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayload(null);
    setCopied(false);

    void fetchStediResponse(caseId)
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load Stedi response");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, caseId]);

  const formattedJson = useMemo(() => {
    if (!payload?.stedi_eligibility_response) return "";
    return JSON.stringify(payload.stedi_eligibility_response, null, 2);
  }, [payload]);

  const attemptedPayerIds = useMemo(
    () => (payload?.attempted_payer_ids ?? []).filter((id) => id.trim()),
    [payload],
  );

  const handleCopy = useCallback(async () => {
    if (!formattedJson) return;
    try {
      await navigator.clipboard.writeText(formattedJson);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [formattedJson]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl flex-col gap-3">
        <DialogHeader>
          <DialogTitle>Stedi eligibility response</DialogTitle>
          <DialogDescription>
            {caseId ? (
              <>
                Full JSON returned by Stedi for case{" "}
                <span className="font-mono text-xs">{caseId}</span>
                {payload?.enriched_at ? (
                  <>
                    {" "}
                    · enriched{" "}
                    {new Date(payload.enriched_at).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </>
                ) : null}
                {payload?.provider_npi_from_case ||
                payload?.request_provider_npi ? (
                  <>
                    {" "}
                    · case NPI{" "}
                    <span className="font-mono">
                      {payload.provider_npi_from_case ?? "—"}
                    </span>
                    {payload.request_provider_npi &&
                    payload.request_provider_npi !==
                      payload.provider_npi_from_case ? (
                      <>
                        {" "}
                        · last request NPI{" "}
                        <span className="font-mono">
                          {payload.request_provider_npi}
                        </span>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : (
              "Select a case to view its stored Stedi response."
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading Stedi response…
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-6 text-sm text-destructive">
            {error}
          </div>
        ) : payload ? (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                Patient{" "}
                <span className="font-mono">{payload.patient_id}</span>
                {" · "}
                Member <span className="font-mono">{payload.member_id}</span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => void handleCopy()}
                disabled={!formattedJson}
              >
                <Copy className="h-3 w-3" />
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            </div>
            {attemptedPayerIds.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span>Payer IDs tried:</span>
                {attemptedPayerIds.map((id, index) => (
                  <span
                    key={`${id}-${index}`}
                    className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px]"
                  >
                    {id}
                  </span>
                ))}
              </div>
            ) : null}
            <ScrollArea className="h-[min(60vh,520px)] rounded-md border bg-muted/40">
              <pre
                className={cn(
                  "p-4 text-xs leading-relaxed",
                  "whitespace-pre-wrap break-words font-mono",
                )}
              >
                {formattedJson}
              </pre>
            </ScrollArea>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
